const PROMOTION_KEYS = ['dining', 'attractions', 'sports', 'arts'];

export function buildDefaultFilters(initialMarkers, attractionCategories = null) {
  const allCommunityIds = initialMarkers.filter((m) => m.type === 'community').map((m) => m.id);

  // Use categories if available, otherwise fall back to individual attraction IDs
  const allAttractionIds = attractionCategories?.filterOptions
    ? attractionCategories.filterOptions.map((opt) => opt.value)
    : initialMarkers.filter((m) => m.type === 'attraction').map((m) => m.id);

  return {
    hearts: true,
    artists: true,
    events: true,
    communities: { all: true, selected: allCommunityIds },
    attractions: { all: true, selected: allAttractionIds },
    promotions: { all: true, dining: true, attractions: true, sports: true, arts: true },
  };
}

export function buildFiltersFromParams(
  communitySlug,
  typesParam,
  promoParam,
  attrParam,
  initialMarkers,
  attractionCategories = null
) {
  const defaultFilters = buildDefaultFilters(initialMarkers, attractionCategories);
  const filters = {
    ...defaultFilters,
    communities: { ...defaultFilters.communities },
    attractions: { ...defaultFilters.attractions },
    promotions: { ...defaultFilters.promotions },
  };

  const hasTypesParam = typesParam !== null && typesParam !== undefined;
  const types = typesParam ? typesParam.split(',').map((t) => t.trim().toLowerCase()) : [];
  const normalizedTypes = types.filter((t) => t && t !== 'none');

  if (hasTypesParam) {
    filters.hearts = normalizedTypes.includes('hearts');
    filters.artists = normalizedTypes.includes('artists');
    filters.events = normalizedTypes.includes('events');

    const allCommunityIds = defaultFilters.communities.selected;
    const allAttractionIds = defaultFilters.attractions.selected;

    filters.communities = {
      all: normalizedTypes.includes('communities'),
      selected: normalizedTypes.includes('communities') ? allCommunityIds : [],
    };

    const includesAttractions = normalizedTypes.includes('attractions');
    const attrSelections =
      attrParam && attrParam !== 'none'
        ? attrParam
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean)
        : [];

    const normalizedAttrSelections = attrSelections.filter((id) => allAttractionIds.includes(id));

    filters.attractions = includesAttractions
      ? normalizedAttrSelections.length > 0
        ? { all: false, selected: normalizedAttrSelections }
        : { all: true, selected: allAttractionIds }
      : { all: false, selected: [] };

    const includesPromotions = normalizedTypes.includes('promotions');
    const promoTypes =
      promoParam && promoParam !== 'none'
        ? promoParam
            .split(',')
            .map((t) => t.trim().toLowerCase())
            .filter(Boolean)
        : [];

    const activePromoKeys = includesPromotions
      ? promoTypes.length > 0
        ? PROMOTION_KEYS.filter((key) => promoTypes.includes(key))
        : PROMOTION_KEYS
      : [];

    const promoAll = includesPromotions && activePromoKeys.length === PROMOTION_KEYS.length;

    filters.promotions = PROMOTION_KEYS.reduce(
      (acc, key) => {
        acc[key] = includesPromotions && activePromoKeys.includes(key);
        return acc;
      },
      { all: promoAll }
    );
  }

  if (communitySlug) {
    const tokens = communitySlug
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    const selectedIds = [];
    tokens.forEach((token) => {
      const communityMarker = initialMarkers.find(
        (m) =>
          m.type === 'community' &&
          (m.slug === token || m.id === `community-${token}` || m.id === token)
      );
      if (communityMarker && !selectedIds.includes(communityMarker.id)) {
        selectedIds.push(communityMarker.id);
      }
    });

    if (selectedIds.length > 0) {
      filters.communities = {
        all: false,
        selected: selectedIds,
      };
    }
  }

  return Object.keys(filters).length > 0 ? filters : null;
}

export function resolveInitialExploreState(
  initialMarkers,
  urlParams = {},
  attractionCategories = null
) {
  const view = urlParams.view;
  const community = urlParams.community;
  const types = urlParams.types;
  const search = urlParams.search;
  const promo = urlParams.promo;
  const attr = urlParams.attr;
  const showClusters = urlParams.showClusters;
  const fadeIcons = urlParams.fadeIcons;

  const hasFilters = community || types || promo || attr;
  const viewMode =
    view && ['map', 'list'].includes(view) ? view : !view && hasFilters ? 'list' : 'map';

  const filters = hasFilters
    ? buildFiltersFromParams(community, types, promo, attr, initialMarkers, attractionCategories) ||
      buildDefaultFilters(initialMarkers, attractionCategories)
    : buildDefaultFilters(initialMarkers, attractionCategories);

  return {
    viewMode,
    filters,
    searchQuery: search || '',
    showClusters: showClusters ?? true,
    fadeIcons: fadeIcons ?? false,
  };
}

export function hasUrlFilters(urlParams = {}) {
  return Boolean(
    urlParams.community ||
    urlParams.types ||
    urlParams.search ||
    urlParams.promo ||
    urlParams.attr ||
    urlParams.showClusters ||
    urlParams.fadeIcons
  );
}

export function buildUrlParamKey(params = {}) {
  const view = params.view ?? null;
  const community = params.community ?? null;
  const types = params.types ?? null;
  const search = params.search ?? null;
  const promo = params.promo ?? null;
  const attr = params.attr ?? null;
  const showClusters = params.showClusters ?? null;
  const fadeIcons = params.fadeIcons ?? null;
  return `${view}|${community}|${types}|${search}|${promo}|${attr}|${showClusters}|${fadeIcons}`;
}
